import { Button, cn, Link, Spinner, Tab, Tabs } from '@heroui/react'
import CopyButton from 'components/Hero/CopyButton'
import { formatDistanceToNow } from 'date-fns'
import { useAuth } from 'hooks/useAuth'
import { Building, CircleHelp, NotepadText } from 'lucide-react'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import Conditions from './Conditions'
import GroupingsTable from './GroupingsTable'
import Issues from './Issues'
import SchemaAnalysis from './SchemaAnalysis'
import SchemaInfoModal from './SchemaInfoModal'
import { useSchema, useSchemas } from './utils'
import VariableTable from './VariableTable'

function SchemaItem({ item }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const isSelected = searchParams.get('schema') === item.id

  const onSelect = () => {
    searchParams.set('schema', item.id)
    setSearchParams(searchParams)
  }

  return (
    <div
      className={
        cn(
          `
            flex w-full flex-col gap-2 border-2 border-gray-200 bg-white p-3
            transition-all duration-200
          `,
          { 'border-primary-500 shadow-xs': isSelected },
          { 'cursor-pointer hover:bg-gray-100': !isSelected },
        )
      }
      onClick={onSelect}
    >
      <span className="flex w-full items-start justify-between gap-2">
        <h4 className="font-taurus text-sm font-medium">{item.name}</h4>
        <p className="text-2xs text-gray-500">
          {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
        </p>
      </span>
      <p className="text-xs text-gray-500">{item.description}</p>
    </div>
  )
}

export default function CitationSchemas() {
  const [searchParams, setSearchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { org } = useAuth()
  const { data: schemas, isLoading } = useSchemas()
  const { data: selectedSchema } = useSchema(id)

  const isEnterprise = org?.org_type === 'enterprise'

  useEffect(() => {
    if (!isLoading && schemas?.length > 0 && !id) {
      searchParams.set('schema', schemas[0].id)
      setSearchParams(searchParams)
    }
  }, [isLoading])

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Spinner />
      </div>
    )
  }

  const renderDescription = (tab) => {
    let helpText = ''

    switch (tab) {
      case 'variables':
        helpText = 'Variables are used to extract specific information from calls or assess subjective quality aspects.'
        break
      case 'groupings':
        helpText = 'Groupings are used to group variables together. Grouping related variables can lead to more accurate citations.'
        break
      case 'conditions':
        helpText = 'Conditions are used to specify variables that should be extracted based on the evaluation of other variables.'
        break
      case 'issues':
        helpText = 'Issue flags automatically identify and categorize problems in your calls based on extracted variable values.'
        break
      default:
        helpText = ''
    }

    return (
      <p className="inline-flex items-center gap-1 py-2 text-gray-500">
        <CircleHelp size={12} />
        {helpText}
      </p>
    )
  }

  return (
    <div className={`
      scrollbar-hide flex h-full max-h-full w-full max-w-full flex-row
      overflow-y-scroll
    `}
    >
      <div className={`
        sticky top-0 flex h-full w-1/3 max-w-(--breakpoint-sm) flex-col gap-2
        overflow-y-auto border-r bg-gray-50 p-3
      `}
      >
        {schemas?.length > 0
          ? (
              <>
                {schemas.map(schema => (
                  <SchemaItem
                    key={schema.id}
                    item={schema}
                  />
                ))}
                <SchemaInfoModal />
              </>
            )
          : (
              <div className={`
                flex h-full w-full flex-col items-center justify-center
              `}
              >
                <p className="mb-6 text-center text-sm text-gray-500">
                  Your schemas will appear here
                </p>
              </div>
            )}
      </div>

      {!isEnterprise
        ? (
            <div className={`
              mx-auto mt-40 flex h-fit w-full max-w-(--breakpoint-sm) flex-col
              items-center
            `}
            >
              <Building size={64} className="text-gray-800" />
              <h1 className="mb-4 text-3xl font-semibold tracking-tighter">
                Citation schemas are an enterprise feature
              </h1>
              <p className="mb-10 text-center text-base text-gray-500">
                Citation schemas are used to extract timestamped citations from calls with AI. Define the variables you want to extract, and the conditions under which to extract them.
              </p>
              <Button as={Link} href="/enterprise" color="primary" variant="solid" size="lg" fullWidth radius="sm">
                Contact sales
              </Button>
            </div>
          )
        : !schemas?.length
            ? (
                <div className={`
                  mx-auto mt-40 flex h-fit w-full max-w-(--breakpoint-sm)
                  flex-col items-center p-5
                `}
                >
                  <NotepadText size={64} className="text-gray-800" />
                  <h1 className="mb-4 text-3xl font-semibold tracking-tighter">
                    Create your first citation schema
                  </h1>
                  <p className="mb-10 text-center text-base text-gray-500">
                    Citation schemas are used to extract timestamped citations from calls with AI. Define the variables you want to extract, and the conditions under which to extract them.
                  </p>
                  <SchemaInfoModal />
                </div>
              )
            : selectedSchema && (
              <div className={`
                mx-auto flex max-h-full w-2/3 flex-col items-start p-7
              `}
              >
                <span className={`
                  mb-4 flex w-full flex-col gap-2 border-b-2 border-gray-200
                  pb-2
                `}
                >
                  <span className="flex w-full flex-row items-center gap-4">
                    <h1 className={`
                      font-taurus text-3xl font-bold tracking-tight
                    `}
                    >
                      {selectedSchema?.name}
                    </h1>
                    <CopyButton color="default" variant="light" value={selectedSchema?.id} displayText={`${selectedSchema?.id.slice(0, 8)}...`} />
                    <SchemaInfoModal isEdit className="ml-auto" />
                  </span>
                  <p className="text-sm text-gray-500">{selectedSchema?.description}</p>
                </span>
                <Tabs radius="none" variant="light" color="primary" classNames={{ panel: 'w-full pt-2 flex flex-col items-start gap-4' }}>
                  <Tab key="variables" title="Variables">
                    {renderDescription('variables')}
                    <VariableTable />
                  </Tab>
                  <Tab key="groupings" title="Groupings">
                    {renderDescription('groupings')}
                    <GroupingsTable />
                  </Tab>
                  <Tab key="conditions" title="Conditions">
                    {renderDescription('conditions')}
                    <Conditions />
                  </Tab>
                  <Tab key="issues" title="Issues">
                    {renderDescription('issues')}
                    <Issues />
                  </Tab>
                  <Tab key="analysis" title="Analysis">
                    <SchemaAnalysis />
                  </Tab>
                </Tabs>
              </div>
            )}
    </div>
  )
}
